import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * The last line before a white screen.
 *
 * A rendering error anywhere in the tree otherwise unmounts everything and
 * leaves a blank page with the explanation in a console nobody has open.
 * This catches it, says so in plain words, and offers the one action that
 * reliably helps. It is deliberately not translated: the translation layer
 * is part of the tree that may have just failed.
 */
interface State {
  error?: Error;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = {};

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Interface error', error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div
        className="app app--waiting"
        style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', padding: 24 }}
      >
        <div className="auth__card stack" role="alert">
          <h1>Something went wrong on this page</h1>
          <p className="muted">
            Your rolls and photographs are safe on the server; only the page needs restarting.
          </p>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, opacity: 0.7 }}>{this.state.error.message}</pre>
          <button type="button" className="btn btn--primary" onClick={() => window.location.reload()}>
            Reload
          </button>
        </div>
      </div>
    );
  }
}
